import * as Y from 'yjs';

import { type BaseErrorOptions } from '@canvas/error';
import { RecoverableNetworkError, UnrecoverableNetworkError } from '@canvas/retry';
import { type TokenManager } from '@canvas/token-manager';

import { type CollabStrategy, getCollabStrategy } from '../collab-strategy/index.ts';

import { fetchWithTimeout } from './fetch-with-timeout.ts';

export class BoardDocumentMissingError extends UnrecoverableNetworkError {
  constructor(options?: BaseErrorOptions) {
    super('document does not exist', 'not-found', {
      name: 'BoardDocumentMissingError',
      ...options,
    });
  }
}

export class NoConnectionError extends RecoverableNetworkError {
  constructor(options?: BaseErrorOptions) {
    super('no connection to canvas servers', { name: 'NoConnectionError', ...options });
  }
}

/**
 * Thrown when canvas-server returns 423 because the document is locked for a
 * collab-infrastructure migration. Recoverable like the websocket SESSION_LOCKED
 * close, but distinguishable so the client drives the same "board locked" UX
 * instead of the generic offline/backoff path.
 */
export class DocumentLockedError extends RecoverableNetworkError {
  constructor(options?: BaseErrorOptions) {
    super('document is locked for collab-infrastructure migration', {
      name: 'DocumentLockedError',
      ...options,
    });
  }
}

interface DocumentRequestOptions {
  serverUrl: string;
  documentId: string;
  signal: AbortSignal;
  headers?: Record<string, string>;
  tokenManager: TokenManager;
  versionId?: string;
}

const parsePersistedSnapshotSizeBytes = (value: string | null): number | undefined => {
  if (value === null || value.trim() === '') {
    return;
  }

  const size = Number(value);
  return Number.isSafeInteger(size) && size >= 0 ? size : undefined;
};

export const fetchInitialDocumentStateV2 = async ({
  serverUrl,
  documentId,
  headers,
  signal,
  tokenManager,
  versionId,
}: DocumentRequestOptions) => {
  const token = await tokenManager.tokenForDocumentIdOrThrow(documentId);
  const opts: RequestInit = {
    headers: {
      'x-access-token': token.accessToken,
      ...headers,
    },
    signal,
  };

  const params = {
    skipSteps: 'true',
    v: '2',
    ...(versionId && versionId !== 'latest' && { versionId }),
  };
  const searchParams = new URLSearchParams(params);
  const canvasServerUrl = `${serverUrl}/api/_internal/collab/${encodeURIComponent(
    documentId,
  )}?${searchParams}`;

  const result = await fetchWithTimeout(canvasServerUrl, opts, {
    timeout: 60000,
  });

  if (!result.ok) {
    const meta = { operation: 'initial-document', status: result.status };
    if (result.status === 401) {
      throw new RecoverableNetworkError(
        `Expired token attempting to fetch document ${documentId}`,
        { name: 'RecoverableNetworkError', meta },
      );
    } else if (result.status === 403) {
      throw new UnrecoverableNetworkError(
        `Not allowed to perform initial document fetch. Access denied.`,
        'unknown',
        { name: 'UnrecoverableNetworkError', meta },
      );
    } else if (result.status === 404) {
      throw new BoardDocumentMissingError({ name: 'BoardDocumentMissingError', meta });
    } else if (result.status === 408) {
      throw new RecoverableNetworkError(
        `Timed out attempting to fetch document ${documentId}`,
        { name: 'RecoverableNetworkError', meta },
      );
    } else if (result.status === 423) {
      // Locked for a collab-infrastructure migration: throw a recoverable
      // DocumentLockedError so the socket layer retries until the migration
      // completes, driving the same "board locked" UX as the websocket path.
      throw new DocumentLockedError({ name: 'DocumentLockedError', meta });
    } else if (result.status >= 500) {
      throw new RecoverableNetworkError(
        `Unable to establish connection for document ${documentId} but can retry`,
        { name: 'RecoverableNetworkError', meta },
      );
    } else {
      throw new RecoverableNetworkError(
        `Unable to establish connection for document ${documentId}`,
        { name: 'RecoverableNetworkError', meta },
      );
    }
  }

  const documentBuffer = await result.arrayBuffer();
  const headStep = result.headers.get('X-Server-Head-Step');
  const untrustedSnapshot = result.headers.get('X-Server-Snapshot-Untrusted') === 'true';
  const persistedSnapshotSizeBytes = parsePersistedSnapshotSizeBytes(
    result.headers.get('X-Server-Persisted-Snapshot-Size-Bytes'),
  );
  // canvas-server sets the header based on the existing collab infra set for this document
  const collabInfrastructureHeader = result.headers.get('X-Collab-Infrastructure');
  const useNcs = collabInfrastructureHeader === 'ncs';
  const collabStrategy = getCollabStrategy(useNcs ? 'ncs' : 'cf');

  if (!documentBuffer) {
    throw new RecoverableNetworkError(`Response did not specify a relay to join`);
  }

  return {
    document: new Uint8Array(documentBuffer),
    persistedSnapshotSizeBytes,
    headStep,
    untrustedSnapshot,
    collabStrategy,
  };
};

export type GetDocumentV2Opts = {
  documentId: string;
  serverUrl: string | undefined;
  signal: AbortSignal;
  tokenManager: TokenManager;
  headers?: Record<string, string>;
  versionId?: string;
  handleUntrustedSnapshot?: (untrusted: boolean) => void;
};

export type RemoteDocument = {
  document: Y.Doc;
  /**
   * Size of the encoded Object Store snapshot loaded by canvas-server.
   */
  persistedSnapshotSizeBytes?: number;
  headStep: string | null;
  collabStrategy: CollabStrategy;
};

export async function getDocumentV2({
  documentId,
  serverUrl,
  signal,
  tokenManager,
  headers,
  versionId,
  handleUntrustedSnapshot,
}: GetDocumentV2Opts): Promise<RemoteDocument> {
  const document = new Y.Doc();
  if (serverUrl) {
    const result = await fetchInitialDocumentStateV2({
      serverUrl: serverUrl,
      documentId,
      headers,
      signal,
      tokenManager,
      versionId,
    });

    if (handleUntrustedSnapshot) {
      handleUntrustedSnapshot(result.untrustedSnapshot);
    }

    Y.applyUpdate(document, result.document);
    return {
      document,
      persistedSnapshotSizeBytes: result.persistedSnapshotSizeBytes,
      headStep: result.headStep,
      collabStrategy: result.collabStrategy,
    };
  }

  const collabStrategy = getCollabStrategy('cf');
  return { document, headStep: null, collabStrategy };
}
